import React, { useEffect, useRef, useState } from "react";
import { Input } from "./Input";
import { useApi } from "../Helpers/ApiHook";
import REGEX from "../Constants/Regex";

export function UserNameInput({
    value,
    onChange,
    disabled = false,
    errors,
    category = "trainer",
    user_id = null,
    onStatusChange = () => { }, // optional callback
    ...rest
}) {
    const { get } = useApi();
    const [status, setStatus] = useState("idle"); // idle | tooShort | checking | available | taken | invalid | error
    const [message, setMessage] = useState("");

    // store the last input value (current text) and last checked username
    const [lastValue, setLastValue] = useState("");
    const [lastCheckedUsername, setLastCheckedUsername] = useState(null);

    const debounceRef = useRef(null);
    const abortRef = useRef(null);
    const mountedRef = useRef(false); // <-- skip first onStatusChange call

    const USERNAME_REGEX = /^[a-z1-9_]+$/;
    const MIN_LENGTH = 3; // only check after this many characters

    const handleInputChange = (e) => {
        if (onChange) onChange(e);

        // sanitize using your REGEX (assumed to remove invalid chars)
        const raw = (e.target.value || "");
        const val = raw.trim().replace(REGEX.USER_NAME_REGEX, "");
        setLastValue(val);

        // clear previous timers/requests
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (abortRef.current) {
            try { abortRef.current.abort(); } catch { }
            abortRef.current = null;
        }

        // empty => reset
        if (!val) {
            setStatus("idle");
            setMessage("");
            return;
        }

        // local regex validation
        if (!USERNAME_REGEX.test(val)) {
            setStatus("invalid");
            setMessage(
                "Only lowercase letters (a–z), digits (1–9) & underscore (_) allowed."
            );
            return;
        }

        // Enforce "after MIN_LENGTH characters" rule
        if (val.length < MIN_LENGTH) {
            setStatus("tooShort");
            setMessage(`Type at least ${MIN_LENGTH} characters to check availability.`);
            return;
        }

        // otherwise proceed to check
        setStatus("checking");
        setMessage("Checking…");

        debounceRef.current = setTimeout(() => verifyUserName(val), 400);
    };

    const verifyUserName = async (username) => {
        const controller = new AbortController();
        abortRef.current = controller;

        const url = `/user-name-verify/${encodeURIComponent(username)}`;

        try {
            const res = await get(url, {
                signal: controller.signal,
                params: {
                    category: encodeURIComponent(category),
                    user_id: user_id
                }
            });
            // support both parsed object or Response-like
            const data = res && typeof res.json === "function" ? await res.json() : res;

            setLastCheckedUsername(username);

            if (data?.isExist) {
                setStatus("taken");
                setMessage("Username already taken.");
            } else {
                setStatus("available");
                setMessage("Username available.");
            }
        } catch (err) {
            if (err?.name === "AbortError") {
                // request aborted — do nothing
                return;
            }
            setStatus("error");
            setMessage("Unable to verify username.");
        } finally {
            // clear abortRef if it's our controller
            if (abortRef.current === controller) abortRef.current = null;
        }
    };

    // call onStatusChange whenever status (or lastCheckedUsername/lastValue) changes
    useEffect(() => {
        // skip first run on mount
        if (!mountedRef.current) {
            mountedRef.current = true;
            return;
        }

        // derive isExist from status (true when taken, false when available)
        let isExist = false;
        if (status === 'idle' && !!(value || "").trim()) isExist = true;
        if (status === "taken") isExist = false;
        else if (status === "available") isExist = true;

        // prefer the username that was actually checked; fall back to current value
        const username = lastCheckedUsername ?? lastValue ?? "";

        try {
            onStatusChange({ status, isExist, username });
        } catch (e) {
            // swallow callback errors
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, lastCheckedUsername, lastValue]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
            if (abortRef.current) {
                try { abortRef.current.abort(); } catch { }
                abortRef.current = null;
            }
        };
    }, []);

    // BORDER COLORS
    const borderClass =
        status === "invalid" || status === "taken"
            ? "border-red-500"
            : status === "available"
                ? "border-green-500"
                : status === "checking"
                    ? "border-yellow-500"
                    : status === "tooShort"
                        ? "border-yellow-300"
                        : "";

    // SVG ICONS (no icon for tooShort/idle)
    const renderSvg = () => {
        if (status === "checking") {
            return (
                <svg
                    className="w-5 h-5 animate-spin text-yellow-500"
                    viewBox="0 0 24 24"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    />
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 00-10 10h4z"
                    />
                </svg>
            );
        }

        if (status === "available") {
            return (
                <svg
                    className="w-5 h-5 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                    />
                </svg>
            );
        }

        if (status === "invalid" || status === "taken" || status === "error") {
            return (
                <svg
                    className="w-5 h-5 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                    />
                </svg>
            );
        }

        return null;
    };

    return (
        <div className="w-full">
            <div className="relative">
                <Input
                    label="User Name"
                    subTitle="Valid: lowercase letters (a–z), numbers (1–9), underscore (_)"
                    placeholder="Enter Your User Name"
                    name="user_name"
                    value={value}
                    onChange={handleInputChange}
                    disabled={disabled}
                    errors={errors}
                    className={`pr-12 ${borderClass}`}
                    {...rest}
                />

                {/* SVG ICON INSIDE INPUT (only when renderSvg returns something) */}
                {renderSvg() && (
                    <span className="absolute right-3 top-3/4 pt-3 sm:pt-2 md:pt-1 -translate-y-1/2 pointer-events-none">
                        {renderSvg()}
                    </span>
                )}
            </div>

            <p
                className={`text-sm mt-1 ${status === "available"
                    ? "text-green-600"
                    : status === "tooShort"
                        ? "text-yellow-600"
                        : status === "invalid" || status === "taken" || status === "error"
                            ? "text-red-600"
                            : "text-gray-500"
                    }`}
            >
                {message}
            </p>
        </div>
    );
}
